import { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useWatch, type Control } from "react-hook-form";
import NotificationDispatch, { showErrorNotification } from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import type { DeckDesignerResult } from "../../../http/deckDesigner/formulas";
import type { ReportImage, ReportLabel } from "../../../http/deckDesigner/types/report";
import type { DeckDesignerConfigurationFormValues } from "../../../http/deckDesigner/useAutoSaveDeckDesignerConfiguration";
import { useDeckDesignerReportApi } from "../../../http/deckDesigner/useReport";
import type { Language } from "../../../types";
import type { BillRow, ExportAction, PdfExportAction } from "./billOfQuantities.types";
import { buildBillOfQuantitiesRows } from "./billOfQuantitiesRows";
import { FIT_BOUNDS_PADDING_RATIO } from "./CartesianPlane";
import { isConfigurationComplete } from "./configurationCompleteness";
import {
  createDeckBottomViewDxf,
  createDeckConnectorsViewDxf,
  downloadDxfFile,
  getDeckDxfFileName,
} from "./deckBottomViewDxf";
import { buildDeckReportPayload } from "./deckReportPayload";
import { SUPPORT_FAMILY_IMAGE_BY_CODE } from "./familyImages";
import { createMeasurementFormat } from "./measurementFormat";
import type { CapturedPreviewImage, FinalReportImageKind } from "./previewCapture";
import {
  encodeCanvasAsPngBase64,
  loadCachedPublicImageAsBase64,
  orientCanvasForFullPage,
  remapLabelPositionForRotation,
} from "./reportImages";
import { createReportValues } from "./reportValues";
import { useDeckBottomViewGeometry } from "./useDeckBottomViewGeometry";

const DEFAULT_PDF_FILE_NAME = "deck";
// Reference photo of the PDF report accessories the page shows too, keyed by the accessory's name
// there (internal/export/deck_designer/report.go). The report-only ones (board sizes, other
// accessories) are embedded server side (internal/export/deck_designer/images.go).
const ACCESSORY_IMAGE_URL_BY_NAME: Record<string, string> = {
  NAG: "/deckDesigner/nag.png",
  GRANULO: "/deckDesigner/granulo.png",
};
// The top-view capture is fitted (via CartesianPlane's fitBounds/FIT_BOUNDS_PADDING_RATIO) so the
// shape fills all but a padding-ratio margin on each side of the image; this is that margin's
// complement, i.e. the fraction of the image's longer dimension the shape itself occupies.
const TOP_VIEW_CONTENT_FRACTION = 1 / (1 + 2 * FIT_BOUNDS_PADDING_RATIO);
// What the report shows, in capture order. The plain bottom view only illustrates the support
// reactions, so it's captured only when the report has them.
const REPORT_IMAGE_KINDS: FinalReportImageKind[] = [
  "bottom_view",
  "bottom_view_plain",
  "top_view",
  "side_view",
  "slope_view",
];

const getFinalReportFileName = (configurationName: string | null | undefined): string => {
  const sanitizedName = configurationName?.trim().replace(/[^a-z0-9-_]+/gi, "-") || "";

  return `${sanitizedName || DEFAULT_PDF_FILE_NAME}-bill-of-quantities.pdf`;
};

const encodeIfCaptured = (canvas: HTMLCanvasElement | undefined): Promise<string | null> =>
  canvas ? encodeCanvasAsPngBase64(canvas) : Promise.resolve(null);

export const useBillOfQuantities = (
  // The saved calculation the PDF report is exported for (the server checks the user can read it).
  calculationId: string | undefined,
  result: DeckDesignerResult,
  configurationName: string | undefined,
  projectName: string | undefined,
  // Captures the given report images (see REPORT_IMAGE_KINDS).
  onCaptureImages: (kinds: FinalReportImageKind[]) => Promise<CapturedPreviewImage[]>,
  control: Control<DeckDesignerConfigurationFormValues>,
  // Passed in, not read via useFormState: that would re-render on every validation (see useFieldsInError).
  hasFormErrors: boolean,
): { rows: BillRow[]; hasAnyResult: boolean; exportActions: ExportAction[]; pdfExport: PdfExportAction } => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const common = i18n.translation.common;
  const calculation = i18n.translation.deckDesignerModule.configurations.calculation;
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  // The guard: the state above only drives the button, and a second click before it re-renders would
  // still read false - a ref is set synchronously, so a double click can't start two exports.
  const isGeneratingPdfRef = useRef(false);
  const { downloadDeckDesignerReport } = useDeckDesignerReportApi();

  const groovingType = useWatch({ control, name: "groovingType", exact: true });
  const groovingHeight = useWatch({ control, name: "groovingHeight", exact: true });
  const longDifferentBoardWidth = useWatch({ control, name: "longDifferentBoardWidth", exact: true });
  const shortDifferentBoardWidth = useWatch({ control, name: "shortDifferentBoardWidth", exact: true });
  const slopeDegrees = useWatch({ control, name: "slopeDegrees", exact: true });
  const slopeArrow = useWatch({ control, name: "slopeArrow", exact: true });
  const unitSystem = useWatch({ control, name: "unitSystem", exact: true });
  const measurementFormat = useMemo(() => createMeasurementFormat(unitSystem), [unitSystem]);
  const reportValues = useMemo(() => createReportValues(measurementFormat), [measurementFormat]);

  const geometry = useDeckBottomViewGeometry(result, control);
  const { boardSegments, bottomLayout, connectorPoints, coordinates, joistWidthMeters } = geometry;

  const canDownloadSupportsDxf =
    !!result?.support &&
    coordinates.length >= 3 &&
    (boardSegments.length > 0 || bottomLayout.joists.length > 0 || bottomLayout.supports.length > 0);
  const canDownloadConnectorsDxf =
    !!result?.connector &&
    coordinates.length >= 3 &&
    (boardSegments.length > 0 || bottomLayout.joists.length > 0 || connectorPoints.length > 0);
  const dxfSupportsDownloadLabel = `${common.download} DXF ${common.supports}`;
  const dxfConnectorsDownloadLabel = `${common.download} DXF ${i18n.translation.deckDesignerModule.connectors.plural}`;

  const handleDownloadSupportsDxf = () => {
    if (!canDownloadSupportsDxf) {
      return;
    }

    downloadDxfFile(
      createDeckBottomViewDxf({
        boards: boardSegments,
        outlineCoordinates: coordinates,
        joists: bottomLayout.joists,
        joistWidthMeters,
        legendLabels: {
          boards: calculation.boards,
          joists: calculation.joists,
          supports: common.supports,
        },
        supports: bottomLayout.supports,
      }),
      getDeckDxfFileName(configurationName, "support-view"),
    );
  };

  const handleDownloadConnectorsDxf = () => {
    if (!canDownloadConnectorsDxf) {
      return;
    }

    downloadDxfFile(
      createDeckConnectorsViewDxf({
        boards: boardSegments,
        connectors: connectorPoints,
        outlineCoordinates: coordinates,
        joists: bottomLayout.joists,
        joistWidthMeters,
        legendLabels: {
          boards: calculation.boards,
          joists: calculation.joists,
          connectors: i18n.translation.deckDesignerModule.connectors.plural,
        },
      }),
      getDeckDxfFileName(configurationName, "connector-view"),
    );
  };

  // The table in the UI language; the report payload is language-neutral: the server names and
  // formats it in the export language.
  const productRows = buildBillOfQuantitiesRows(result, i18n.translation, measurementFormat, reportValues);
  const hasAnyResult =
    result.deckArea != null ||
    result.boardQuantity != null ||
    result.substructureQuantity != null ||
    result.supportQuantity != null ||
    productRows.length > 0;

  const pdfDownloadLabel = calculation.downloadPdf;
  const configurationComplete = isConfigurationComplete(result, groovingType, hasFormErrors);
  // The board fit warning (BoardFitWarning) means boards and gap don't fill the deck exactly: the
  // report would document a layout the user still has to fix, so the PDF waits for it too.
  const hasBoardFitIssue = !!result.boardFit?.hasIssue;
  const canDownloadPdf = !!calculationId && productRows.length > 0 && configurationComplete && !hasBoardFitIssue;
  const pdfDisabledHint =
    productRows.length === 0 || !configurationComplete
      ? calculation.downloadPdfDisabledHint
      : hasBoardFitIssue
        ? calculation.downloadPdfDisabledBoardFitHint
        : null;

  const handleDownloadPdf = async (language: Language) => {
    if (!calculationId || !canDownloadPdf || isGeneratingPdfRef.current) {
      return;
    }

    isGeneratingPdfRef.current = true;
    setIsGeneratingPdf(true);
    try {
      const exportLanguageCode = language.value;
      const exportData = buildDeckReportPayload({
        result,
        rows: productRows,
        values: {
          groovingType,
          groovingHeight,
          longDifferentBoardWidth,
          shortDifferentBoardWidth,
          slopeDegrees,
          slopeArrow,
        },
        geometry,
        measurementFormat,
        reportValues,
      });
      const hasSupportReactions = !!exportData.report.supportReactions;

      const accessoryImageEntries = Object.entries(ACCESSORY_IMAGE_URL_BY_NAME);
      const supportFamilyImageUrl =
        hasSupportReactions && result?.support
          ? SUPPORT_FAMILY_IMAGE_BY_CODE[result.support.supportFamily.code]
          : undefined;
      const [capturedImages, accessoryImages, supportFamilyImage] = await Promise.all([
        onCaptureImages(
          hasSupportReactions ? REPORT_IMAGE_KINDS : REPORT_IMAGE_KINDS.filter((kind) => kind !== "bottom_view_plain"),
        ),
        Promise.all(accessoryImageEntries.map(([, url]) => loadCachedPublicImageAsBase64(url))),
        supportFamilyImageUrl ? loadCachedPublicImageAsBase64(supportFamilyImageUrl) : Promise.resolve(null),
      ]);
      const canvasByKind = Object.fromEntries(capturedImages.map((image) => [image.kind, image.canvas])) as Partial<
        Record<FinalReportImageKind, HTMLCanvasElement>
      >;

      // Rotated when that lets it fill its dedicated page better.
      const bottomView = canvasByKind.bottom_view ? orientCanvasForFullPage(canvasByKind.bottom_view) : null;
      // Each image encoded once, all at the same time.
      const [topViewData, sideViewData, bottomViewData, supportReactionsData, slopeViewData] = await Promise.all([
        encodeIfCaptured(canvasByKind.top_view),
        encodeIfCaptured(canvasByKind.side_view),
        encodeIfCaptured(bottomView?.canvas),
        // The unrotated bottom view, should the plain one be missing.
        hasSupportReactions
          ? encodeIfCaptured(canvasByKind.bottom_view_plain ?? canvasByKind.bottom_view)
          : Promise.resolve(null),
        encodeIfCaptured(canvasByKind.slope_view),
      ]);
      const image = (data: string | null, labels?: ReportLabel[]): ReportImage | null =>
        data == null ? null : { data, labels };

      const today = new Date();
      await downloadDeckDesignerReport(
        calculationId,
        {
          ...exportData.report,
          language: exportLanguageCode,
          date: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`,
          configurationName: configurationName ?? "",
          projectName: projectName ?? "",
          topView:
            topViewData != null
              ? { ...image(topViewData, exportData.topViewLabels)!, contentFraction: TOP_VIEW_CONTENT_FRACTION }
              : null,
          sideView: image(sideViewData),
          bottomView:
            bottomView && bottomViewData != null
              ? {
                  data: bottomViewData,
                  labels: bottomView.rotated
                    ? exportData.bottomViewLabels.map(remapLabelPositionForRotation)
                    : exportData.bottomViewLabels,
                }
              : null,
          supportReactions: exportData.report.supportReactions
            ? {
                ...exportData.report.supportReactions,
                supportFamilyImage,
                image: image(supportReactionsData, exportData.supportReactionLabels),
              }
            : null,
          slopeView: image(slopeViewData),
          accessoryImages: Object.fromEntries(
            accessoryImageEntries.flatMap(([name], index) =>
              accessoryImages[index] ? [[name, accessoryImages[index]]] : [],
            ),
          ),
        },
        getFinalReportFileName(configurationName),
      );
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      isGeneratingPdfRef.current = false;
      setIsGeneratingPdf(false);
    }
  };

  // Everything returned keeps its identity while its content is unchanged, so the memoized result cards
  // skip re-rendering: the rows by content, the actions through stable callbacks to the latest handlers.
  const rowsKey = JSON.stringify(productRows);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const rows = useMemo(() => productRows, [rowsKey]);

  const latestHandlersRef = useRef({ handleDownloadPdf, handleDownloadSupportsDxf, handleDownloadConnectorsDxf });
  useLayoutEffect(() => {
    latestHandlersRef.current = { handleDownloadPdf, handleDownloadSupportsDxf, handleDownloadConnectorsDxf };
  });
  const downloadPdf = useCallback((language: Language) => latestHandlersRef.current.handleDownloadPdf(language), []);
  const downloadSupportsDxf = useCallback(() => latestHandlersRef.current.handleDownloadSupportsDxf(), []);
  const downloadConnectorsDxf = useCallback(() => latestHandlersRef.current.handleDownloadConnectorsDxf(), []);

  const exportActions = useMemo(
    (): ExportAction[] =>
      hasAnyResult
        ? [
            { label: dxfSupportsDownloadLabel, disabled: !canDownloadSupportsDxf, onClick: downloadSupportsDxf },
            { label: dxfConnectorsDownloadLabel, disabled: !canDownloadConnectorsDxf, onClick: downloadConnectorsDxf },
          ]
        : [],
    [
      canDownloadConnectorsDxf,
      canDownloadSupportsDxf,
      downloadConnectorsDxf,
      downloadSupportsDxf,
      dxfConnectorsDownloadLabel,
      dxfSupportsDownloadLabel,
      hasAnyResult,
    ],
  );

  const pdfExport = useMemo(
    (): PdfExportAction => ({
      label: pdfDownloadLabel,
      disabled: !canDownloadPdf || isGeneratingPdf,
      disabledHint: pdfDisabledHint,
      isLoading: isGeneratingPdf,
      download: downloadPdf,
    }),
    [downloadPdf, isGeneratingPdf, canDownloadPdf, pdfDisabledHint, pdfDownloadLabel],
  );

  return { rows, hasAnyResult, exportActions, pdfExport };
};
